// src/pages/billing/fee-policies/tabs/history.tsx
import { Alert, Box, Stack } from "@mantine/core";
import { IconHistory, IconInfoCircle } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import useSWR from "swr";
import { TenantFeePolicyApi } from "../../../../apis";
import type { FeePolicy } from "../../../../apis/tenantFeePolicy";
import { EmptyState } from "../../../../components/billing/EmptyState";
import { PolicyHistoryCard } from "../../../../components/billing/PolicyHistoryCard";
import { PolicyReadOnlyDrawer } from "../../../../components/billing/PolicyReadOnlyDrawer";
import { VersionCompareDrawer } from "../../../../components/billing/VersionCompareDrawer";

const FeePolicyHistoryTab = () => {
  // List all DEFAULT-scope policies (each row in the list IS a version per backend).
  const { data } = useSWR("swr.my.fee-policies.history.default", () =>
    TenantFeePolicyApi.list({ scope: "DEFAULT", page: 1, limit: 100 })
  );
  // Sort newest first by version (or by effectiveFrom as fallback)
  const versions = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = data as any;
    const all: FeePolicy[] = Array.isArray(raw) ? raw : (raw?.rows ?? []);
    return [...all].sort((a, b) => {
      const va = a.currentVersion ?? 0;
      const vb = b.currentVersion ?? 0;
      if (va !== vb) return vb - va;
      const da = a.effectiveFrom ? new Date(a.effectiveFrom).getTime() : 0;
      const db = b.effectiveFrom ? new Date(b.effectiveFrom).getTime() : 0;
      return db - da;
    });
  }, [data]);

  const [viewing, setViewing] = useState<FeePolicy | null>(null);
  const [comparing, setComparing] = useState<{ before: FeePolicy; after: FeePolicy } | null>(null);

  return (
    <Box pt="md">
      <Stack gap="md">
        <Alert color="brand" icon={<IconInfoCircle size={16} />} variant="light">
          DEFAULT policy-ийн хувилбарын түүх. Шинэхэн нь дээр.
        </Alert>

        {versions.length === 0 && <EmptyState icon={<IconHistory size={26} />} message="Хувилбар олдсонгүй." />}

        {versions.map((v, i) => {
          const previous = versions[i + 1] ?? null;
          return (
            <PolicyHistoryCard
              key={v.id}
              version={v}
              previous={previous}
              onView={setViewing}
              onCompare={(a, b) => setComparing({ before: a, after: b })}
            />
          );
        })}
      </Stack>

      <PolicyReadOnlyDrawer
        policy={viewing}
        opened={!!viewing}
        onClose={() => setViewing(null)}
        hideEdit
      />
      <VersionCompareDrawer
        before={comparing?.before ?? null}
        after={comparing?.after ?? null}
        opened={!!comparing}
        onClose={() => setComparing(null)}
      />
    </Box>
  );
};

export default FeePolicyHistoryTab;
